"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelDownloader = void 0;
var logging_1 = require("@augloop/logging/dist/src/client/logging");
var idb_1 = require("idb");
var ResourceReturnType;
(function (ResourceReturnType) {
    ResourceReturnType["URL"] = "URL";
    ResourceReturnType["ArrayBuffer"] = "ArrayBuffer";
})(ResourceReturnType || (ResourceReturnType = {}));
var ModelDownloader = /** @class */ (function () {
    function ModelDownloader(hostApp, baseURL) {
        var _this = this;
        this.dbName = 'ALModels_db';
        this.osName = 'ALModels_os';
        this.baseURL = "https://" + baseURL + "/web/" + hostApp;
        // Start log operation
        var updateMetadataOp = new logging_1.Operation({
            operationName: 'UpdateSessionsSinceModelsUsed',
            success: true,
            resultDescription: ''
        });
        updateMetadataOp.start();
        var id;
        var timeoutPromise = new Promise(function (_resolve, reject) {
            var ms = 10000;
            id = setTimeout(function () {
                reject(new Error("Took longer than " + ms + " ms"));
            }, ms);
        });
        var updateTimesSinceUsagePromise = this.accessDB().then(function (db) {
            return db.transaction(_this.osName, 'readwrite').objectStore(_this.osName).openCursor();
        }).then(function updateTimes(cursor) {
            if (!cursor) {
                return;
            }
            var model = cursor.value;
            if (!model.timesSinceUsage) {
                model.timesSinceUsage = 0;
            }
            model.timesSinceUsage++;
            if (model.timesSinceUsage > 5) {
                // Delete unused models
                cursor.delete();
            }
            else {
                // Update number of sessions since model was used
                cursor.update(model);
            }
            return cursor.continue().then(updateTimes);
        });
        this.initPromise = Promise.race([timeoutPromise, updateTimesSinceUsagePromise]).catch(function (err) {
            updateMetadataOp.success = false;
            updateMetadataOp.resultDescription += err.message + " ";
        }).then(function () {
            clearTimeout(id);
            _this.closeDBAndEndLog(updateMetadataOp, "initializing idb storage");
        });
    }
    ModelDownloader.prototype.getResourceAsArrayBuffer = function (modelId, version) {
        // Start log operation
        var getModelABOp = new logging_1.Operation({
            operationName: 'GetModelArrayBuffer',
            success: true,
            resultDescription: '',
            dimension0: modelId,
            dimension1: version
        });
        getModelABOp.start();
        var dict = {};
        return this.getResource(modelId, version, ResourceReturnType.ArrayBuffer, getModelABOp, function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                var value = model.get(key);
                if (value.arrayBuffer) {
                    return value.arrayBuffer().then(function (abValue) {
                        dict[key] = abValue;
                    });
                }
                else {
                    return new Promise(function (resolve, reject) {
                        var reader = new FileReader();
                        reader.readAsArrayBuffer(value);
                        reader.onload = function (event) {
                            var abVal = event.target.result;
                            if (!(abVal instanceof ArrayBuffer)) {
                                reject(new Error('Unable to obtain ArrayBuffer'));
                            }
                            else {
                                dict[key] = abVal;
                                resolve();
                            }
                        };
                    });
                }
            }));
        }).then(function () {
            return dict;
        });
    };
    ModelDownloader.prototype.getResourceAsURL = function (modelId, version) {
        // Start log operation
        var getModelURLOp = new logging_1.Operation({
            operationName: 'GetModelURL',
            success: true,
            resultDescription: '',
            dimension0: modelId,
            dimension1: version
        });
        getModelURLOp.start();
        var dict = {};
        return this.getResource(modelId, version, ResourceReturnType.URL, getModelURLOp, function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                // Translate blob to resourcePath
                dict[key] = URL.createObjectURL(model.get(key));
            }));
        }).then(function () {
            return dict;
        });
    };
    ModelDownloader.prototype.getResource = function (modelId, version, type, logOp, cb) {
        var _this = this;
        return this.initPromise.catch(function (err) {
            logOp.success = false;
            logOp.resultDescription += err.message + " ";
            logOp.resultSignature = 'Issue initializing';
        }).then(function () {
            return _this.getIndexedDBFiles(modelId, version, logOp);
        }).then(function (model) {
            return cb(model);
        }).then(function () {
            _this.closeDBAndEndLog(logOp, "retrieving " + modelId);
        }).catch(function (err) {
            if (logOp.resultDescription !== err.message + " ") {
                logOp.success = false;
                logOp.resultDescription += err.message + " ";
            }
            logOp.resultSignature = "Issue retrieving from CDN or translating to " + type;
            _this.closeDBAndEndLog(logOp, "retrieving " + modelId);
            throw err;
        });
    };
    ModelDownloader.prototype.isSafari = function () {
        // Unable to use IDB with Safari: https://stackoverflow.com/questions/61596671/can-indexeddb-work-inside-an-iframe-in-safari
        if (typeof window !== 'undefined' && window.navigator && window.navigator.userAgent
            && window.navigator.userAgent.toLowerCase().indexOf('safari') > -1
            && window.navigator.userAgent.toLowerCase().indexOf('chrome') <= -1) {
            return true;
        }
        else {
            return false;
        }
    };
    ModelDownloader.prototype.accessDB = function () {
        if (this.isSafari()) {
            this.accessDBPromise = Promise.reject(new Error('Unable to use IDB with Safari'));
        }
        else if (typeof (Proxy) !== 'undefined') {
            if (!this.accessDBPromise) {
                this.accessDBPromise = this.openDBCall().then(function (db) {
                    if (!db) {
                        throw new Error('No database returned from openDB request');
                    }
                    return db;
                });
            }
        }
        else {
            this.accessDBPromise = Promise.reject(new Error('Proxy does not exist, therefore idb not supported'));
        }
        return this.accessDBPromise;
    };
    ModelDownloader.prototype.openDBCall = function () {
        var _this = this;
        return idb_1.openDB(this.dbName, 1, {
            upgrade: function (db) {
                if (db && !db.objectStoreNames.contains(_this.osName)) {
                    db.createObjectStore(_this.osName, { keyPath: 'modelId' });
                }
            },
        });
    };
    ModelDownloader.prototype.endLog = function (logOp, task) {
        if (logOp.success) {
            logOp.resultDescription = "Success " + task;
            logging_1.Log.info(0x222c504f /* tag_8lfbp */, logging_1.LogCategory.CoreDefault, logOp.stop());
        }
        else {
            logOp.resultDescription += "error " + task;
            logging_1.Log.error(0x222c5050 /* tag_8lfbq */, logging_1.LogCategory.CoreDefault, logOp.stop());
        }
    };
    ModelDownloader.prototype.closeDBAndEndLog = function (logOp, task) {
        var _this = this;
        if (this.accessDBPromise) {
            this.accessDBPromise.then(function (db) {
                db.close();
            }).catch(function (err) {
                if (logOp.resultDescription !== err.message + " ") {
                    logOp.success = false;
                    logOp.resultDescription += (err.message ? err.message : 'Error closing db') + " ";
                    logOp.resultSignature = 'Issue closing db';
                }
            }).then(function () {
                _this.accessDBPromise = undefined;
                _this.endLog(logOp, task);
            });
        }
    };
    /**
     * Retrieves files from IDB or, if that does not work, CDN.
     * If error occurs but files are still retrieved, will record error but still resolve with files.
     * @param modelId
     * @param version
     *
     * @returns Model from IDB or CDN.
     */
    ModelDownloader.prototype.getIndexedDBFiles = function (modelId, version, logOp) {
        var _this = this;
        return this.getModel(modelId).then(function (modelObj) {
            // Check if correct version of model
            if (modelObj && modelObj.model && modelObj.version == version) {
                return Promise.resolve(modelObj.model);
            }
            else {
                return Promise.reject(); // Retrieve model from CDN in catch
            }
        }).catch(function (err) {
            if (err) {
                if (logOp.resultDescription !== err.message + " ") {
                    logOp.success = false;
                    logOp.resultDescription += err.message + " ";
                }
                logOp.resultSignature = 'Issue retrieving from DB';
            }
            return _this.downloadFiles(modelId, version);
        }).then(function (model) {
            // Update the timesSinceUsage
            return _this.putModel(modelId, model, version).then(function () {
                return (model);
            }).catch(function (err) {
                // Error putting model in IDB
                if (logOp.resultDescription !== err.message + " ") {
                    logOp.success = false;
                    logOp.resultDescription += err.message + " ";
                }
                logOp.resultSignature = 'Issue adding to DB';
                return (model);
            });
        });
    };
    ModelDownloader.prototype.downloadFiles = function (modelId, version) {
        var _this = this;
        // Download catalog
        return fetch(this.baseURL + "/catalog_" + modelId + "_" + version + ".json").then(function (response) {
            if (response.ok) {
                return response.text();
            }
            else {
                throw new Error("CDN download response status: " + response.status);
            }
        }).then(function (catalog) {
            var catalogInfo = JSON.parse(catalog);
            // URL add-on to resources
            var middleURL = catalogInfo.MajorVersion + "." + catalogInfo.MinorVersion + "/ModelResources";
            var modelInfo = modelId + "/" + version + "/";
            // Download each file from folder
            return Promise.all(catalogInfo.Resources.map(function (catalogElement) {
                if (catalogElement.Name.length > modelInfo.length && catalogElement.Name.substring(0, modelInfo.length) === modelInfo) {
                    catalogElement.Name = catalogElement.Name.substring(modelInfo.length);
                }
                return fetch(_this.baseURL + "/" + middleURL + "/" + modelInfo + catalogElement.Name + "." + catalogElement.Type).then(function (response) {
                    if (response.ok) {
                        return response.blob();
                    }
                    else {
                        throw new Error("CDN download response status: " + response.status);
                    }
                }).then(function (blobFile) {
                    return [catalogElement.Name + "." + catalogElement.Type, blobFile];
                });
            }));
        }).then(function (arr) {
            return new Map(arr);
        });
    };
    ModelDownloader.prototype.getModel = function (modelId) {
        var _this = this;
        return this.accessDB().then(function (db) {
            // Attempt to retrieve file from IndexedDB
            return db.get(_this.osName, modelId);
        });
    };
    // Used to put new models in & update times since usage
    ModelDownloader.prototype.putModel = function (modelId, model, version) {
        var _this = this;
        return this.accessDB().then(function (db) {
            return db.put(_this.osName, { modelId: modelId, model: model, version: version, timesSinceUsage: 0 });
        });
    };
    return ModelDownloader;
}());
exports.ModelDownloader = ModelDownloader;
//# sourceMappingURL=model-downloader.js.map